import {
  type IntegrationRecordData,
  type Result,
  type WhiteboardIntegration,
} from 'javascripts/shared-types';
import { get } from 'lodash';
import Debug from 'debug';

const debug = Debug('whiteboard:ado');

const ADO_REGEX =
  /^https:\/\/dev.azure.com\/((?:[^/]*)\/(?:[^/]*))\/_workitems\/edit\/(\d+)/;

const API_ENDPOINT = 'https://dev.azure.com';

type AdoOrgProject = `${string}/${string}`;
type AdoRecordId = `${string}/${string}/${string}`;

interface AdoWorkItemIdentifier {
  organization: string;
  project: string;
  id: string;
  key: string;
}

interface RequestOptions {
  authenticate?: boolean;
  forceGetFreshToken?: boolean;
  endpoint?: string;
}

type AdoCategory =
  | 'Proposed'
  | 'InProgress'
  | 'Complete'
  | 'Resolved'
  | 'Removed';
interface AdoWorkItemTypeState {
  name: string;
  color: string;
  category: AdoCategory;
}

const AdoCategoryMap: Record<
  AdoCategory,
  IntegrationRecordData['status']['category']
> = {
  Proposed: 'NOT_STARTED',
  InProgress: 'IN_PROGRESS',
  Complete: 'DONE',
  Resolved: 'DONE',
  Removed: 'WONT_DO',
};

interface AdoWorkItemType {
  color: string;
  description: string;
  icon: { id: string; url: string };
  isDisabled: boolean;
  name: string;
  referenceName: string;
  states: AdoWorkItemTypeState[];
  url: string;
}

interface AdoWorkItem {
  id: number;
  fields: {
    'System.WorkItemType': string;
    'System.Title': string;
    'System.State': string;
    'System.AssignedTo'?: {
      uniqueName: string;
      displayName: string;
      url: string;
    };
    'Microsoft.VSTS.Common.Priority'?: string;
  };
  _links: Record<string, any>;
}

class AdoRecordNotFoundError extends Error {}
class AdoRequestError extends Error {}

function requestHeaders(token: string) {
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
}

class AdoIntegration implements WhiteboardIntegration {
  service = 'ado';
  title = 'Azure DevOps';

  getRecordTitle(plural?: boolean, includeTitle = true) {
    return `${includeTitle ? 'Azure DevOps ' : ''}work item${
      plural ? 's' : ''
    }`;
  }

  /**
   * ADO records are identified by organization/project/id
   */
  urlToRecordId(url: string): AdoRecordId | null {
    const match = url.match(ADO_REGEX);
    if (match) {
      return `${match[1]}/${match[2]}` as AdoRecordId;
    }
    return null;
  }

  hasAuth() {
    return aha.hasAuth('ado');
  }

  private async request(path: string, options: RequestOptions = {}) {
    const endpoint = options.endpoint || API_ENDPOINT;
    let response: Response;
    let authFailed = false;

    try {
      const { token } = await aha.auth('ado', {
        useCachedRetry: !options.forceGetFreshToken,
        reAuth: options.authenticate,
      });

      debug('Requesting', `${endpoint}/${path}`);
      response = await fetch(`${endpoint}/${path}`, {
        headers: requestHeaders(token),
      });
      authFailed = response.status === 401 || response.status === 203;
    } catch (err) {
      authFailed = true;
    }

    if (authFailed) {
      if (options.authenticate && !options.forceGetFreshToken) {
        debug('Re-authenticating');
        return this.request(path, { ...options, forceGetFreshToken: true });
      }

      debug('Authentication failed');
      throw new Error(`Error ${response.status}`);
    }

    if (response.status === 404) throw new AdoRecordNotFoundError();
    if (!response.ok) throw new AdoRequestError();

    const data = await response.json();
    return data;
  }

  private readonly _workItemTypes = new Map<string, AdoWorkItemType>();

  /**
   * Get metadata for a work item type and cache it against the instance
   */
  private async getWorkItemType(
    orgProject: AdoOrgProject,
    workItemType: string
  ) {
    const key = `${orgProject}/${workItemType}`;
    if (this._workItemTypes.has(key)) {
      return this._workItemTypes.get(key);
    }

    const data = await this.request(
      `${orgProject}/_apis/wit/workitemtypes/${workItemType}?api-version=5.0`
    );
    this._workItemTypes.set(key, data as AdoWorkItemType);
    return data as AdoWorkItemType;
  }

  private async workItemToRecord(
    orgProject: AdoOrgProject,
    workItem: AdoWorkItem
  ): Promise<IntegrationRecordData> {
    const recordId: AdoRecordId = `${orgProject}/${workItem['id']}`;
    const typeName = get(workItem, ['fields', 'System.WorkItemType']);
    const stateName = get(workItem, ['fields', 'System.State']);

    const type = await this.getWorkItemType(orgProject, typeName);
    if (!type) throw new Error(`Work item type ${typeName} not found`);

    const state = type.states.find(state => state.name === stateName);

    const assignee = get(workItem, ['fields', 'System.AssignedTo']);
    const priority = get(workItem, [
      'fields',
      'Microsoft.VSTS.Common.Priority',
    ]);

    return {
      id: String(workItem['id']),
      recordId,
      name: workItem['fields']['System.Title'],
      url: workItem['_links']['html']['href'],
      status: {
        id: workItem['fields']['System.State'],
        name: workItem['fields']['System.State'],
        color: `#${state?.color || '000000'}`,
        category: state.category ? AdoCategoryMap[state.category] : undefined,
      },
      assignee: assignee
        ? {
            id: assignee['uniqueName'],
            name: assignee['displayName'],
            avatar: get(assignee, '_links.avatar.href'),
          }
        : undefined,
      priority: priority
        ? {
            id: priority,
            name: priority,
          }
        : undefined,
      type: {
        icon: type.icon.url,
        id: type.referenceName,
        name: type.name,
      },
      key: `${type.name} ${workItem['id']}`,
    };
  }

  async getRecords({
    recordUrls,
    options,
  }: {
    recordUrls: string[];
    options?: { authenticate?: boolean };
  }): Promise<Result<IntegrationRecordData[]>> {
    const authenticate = options?.authenticate ?? true;

    debug('Fetching ADO records', recordUrls);

    const recordIds = recordUrls
      .map(u => this.urlToRecordId(u))
      .filter(Boolean);

    if (recordIds.length === 0) return { status: 'success', data: [] };

    const fields = [
      'System.WorkItemType',
      'System.Title',
      'System.State',
      'System.AssignedTo',
      'Microsoft.VSTS.Common.Priority',
    ];

    try {
      await this.request('_apis/profile/profiles/me?api-version=6.0', {
        endpoint: 'https://app.vssps.visualstudio.com',
        authenticate,
      });
    } catch (err) {
      debug('Authentication failed', err);
      return { status: 'error', error: 'Authentication failed' };
    }

    try {
      const groupedByOrgAndProject = new Map<
        AdoOrgProject,
        AdoWorkItemIdentifier[]
      >();

      // Group the record IDs by organization and project as we can only fetch work items in the same project at once
      for (const recordId of recordIds) {
        const segments = recordId.split('/');
        const [organization, project] = segments.slice(0, 2);
        const id = segments[segments.length - 1];
        const orgProjectPath: AdoOrgProject = `${organization}/${project}`;

        if (!groupedByOrgAndProject.has(orgProjectPath)) {
          groupedByOrgAndProject.set(orgProjectPath, []);
        }
        groupedByOrgAndProject
          .get(orgProjectPath)
          .push({ organization, project, id, key: recordId });
      }

      // Construct API paths from the grouped IDs
      const workItemApiPaths: [AdoOrgProject, string][] = [
        ...groupedByOrgAndProject.keys(),
      ].map(key => {
        const ids = groupedByOrgAndProject
          .get(key)
          .map(awi => awi.id)
          .join(',');
        return [
          key,
          `${key}/_apis/wit/workitems?ids=${ids}&fields=${fields.join(
            ','
          )}&$expand=Links&api-version=5.1`,
        ];
      });

      const records: IntegrationRecordData[] = [];

      for (const [orgProject, path] of workItemApiPaths) {
        const data = await this.request(path, { authenticate });
        if (!data['value']) continue;

        for (const workItem of data['value']) {
          try {
            const record = await this.workItemToRecord(orgProject, workItem);
            records.push(record);
          } catch (err) {
            if (err instanceof AdoRecordNotFoundError) {
              // Ignore missing records/types and continue fetching
              console.warn(`ADO Work item ${workItem.id} not found`);
            } else {
              // Anything else should abort the whole fetching process
              throw err;
            }
          }
        }
      }

      return {
        status: 'success',
        data: records,
      };
    } catch (err) {
      return { status: 'error', error: String(err) };
    }
  }
}

export default AdoIntegration;
